/**
 * SnapshotPage — renders a /s/:token URL by fetching the persisted
 * VisualPayload from /share/{token} and handing it to CardRenderer.
 *
 * Mirrors CardPage in spirit (stripped chrome, no chat input) but
 * with no /ask call — the snapshot is the answer, captured at the
 * moment the sharer hit Share.  A prominent banner tells the
 * recipient how old the snapshot is and offers two CTAs:
 *
 *   - "Ask Scout this again now"      → /?ask=<question>  (live re-run)
 *   - "Ask Scout anything →"          → /                  (open chat)
 *
 * Phase C of share-urls — see docs/share-urls-plan.md §5.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import type { VisualPayload } from "../types";
import CardRenderer from "../components/cards/CardRenderer";
import Header from "../components/Header";

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "";

interface SnapshotResponse {
  token: string;
  intent: string;
  payload: VisualPayload;
  voice_text: string | null;
  question: string | null;
  created_at: string;  // ISO 8601
  expires_at: string;  // ISO 8601
  view_count: number;
}

/** Days-since helper for the "Captured X ago" banner.  Returns
 * "today" / "yesterday" / "N days ago" — same grammar as Twitter etc. */
function _agoLabel(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "recently";
  const days = Math.floor((Date.now() - d.getTime()) / 86_400_000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 7) return `${days} days ago`;
  if (days < 30) return `${Math.floor(days / 7)} week${days >= 14 ? "s" : ""} ago`;
  return `${Math.floor(days / 30)} month${days >= 60 ? "s" : ""} ago`;
}

/** Days remaining until expires_at — drives the "expires soon" warning
 * that nudges the sharer to re-share if the snapshot's still relevant. */
function _daysUntil(iso: string): number {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return -1;
  return Math.max(0, Math.ceil((d.getTime() - Date.now()) / 86_400_000));
}

export default function SnapshotPage() {
  const { token = "" } = useParams<{ token: string }>();
  const [data, setData] = useState<SnapshotResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) {
      setError("This share link is malformed.");
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(`${BASE_URL}/share/${token}`, {
          headers: { Accept: "application/json" },
        });
        if (resp.status === 404) {
          if (!cancelled) {
            setError("This snapshot expired or doesn't exist.");
            setLoading(false);
          }
          return;
        }
        if (!resp.ok) {
          if (!cancelled) {
            setError("Couldn't load this snapshot — try again in a moment.");
            setLoading(false);
          }
          return;
        }
        const body = (await resp.json()) as SnapshotResponse;
        if (!cancelled) {
          setData(body);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError("Couldn't load this snapshot — check your connection.");
          setLoading(false);
          console.warn("snapshot fetch failed:", err);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const daysLeft = data ? _daysUntil(data.expires_at) : -1;
  const expiringSoon = daysLeft >= 0 && daysLeft <= 7;
  const captured = data ? _agoLabel(data.created_at) : "";

  // The "ask the same question now" CTA only renders when we know
  // what question produced the snapshot.  Pre-fill the chat with it
  // via the ?ask= search param (read by pages/Chat.tsx).
  const askAgainHref =
    data?.question ? `/?ask=${encodeURIComponent(data.question)}` : null;

  return (
    <>
      <Header league={null} onLeagueChange={() => { /* read-only on snapshots */ }} />

      <main className="flex-1 overflow-y-auto overflow-x-hidden pb-20">
        <div className="max-w-2xl mx-auto px-4 pt-6 pb-12">
          {/* Captured banner — always visible above the card so the
              recipient understands they're looking at a snapshot. */}
          {data && (
            <div
              className={`mb-4 px-4 py-3 rounded-xl border text-xs ${
                expiringSoon
                  ? "bg-amber-500/[0.06] border-amber-500/25 text-amber-200"
                  : "bg-white/[0.03] border-white/[0.08] text-white/70"
              }`}
            >
              <p className="leading-relaxed">
                <strong className="font-medium">Snapshot captured {captured}.</strong>{" "}
                Live data may have changed since.
                {askAgainHref && (
                  <>
                    {" "}
                    <Link
                      to={askAgainHref}
                      className="underline-offset-2 underline hover:text-white transition-colors"
                    >
                      Ask Scout the same question now
                    </Link>
                    .
                  </>
                )}
              </p>
              {expiringSoon && (
                <p className="mt-1 text-[11px] opacity-80">
                  This snapshot expires in {daysLeft === 0 ? "less than a day" : `${daysLeft} day${daysLeft === 1 ? "" : "s"}`}.
                </p>
              )}
            </div>
          )}

          {/* Loading skeleton */}
          {loading && (
            <div
              data-testid="snapshot-page-skeleton"
              className="bg-scout-elevated rounded-2xl p-4 border border-white/[0.08] animate-pulse"
            >
              <div className="h-3 bg-white/10 rounded w-1/3 mb-4" />
              <div className="space-y-2.5">
                <div className="h-4 bg-white/10 rounded w-3/4" />
                <div className="h-4 bg-white/10 rounded w-1/2" />
                <div className="h-4 bg-white/10 rounded w-2/3" />
              </div>
              <div className="h-2 bg-white/[0.06] rounded w-1/4 mt-6" />
            </div>
          )}

          {/* Error state — 404 or fetch failure */}
          {error && !loading && (
            <div
              data-testid="snapshot-page-error"
              className="bg-scout-elevated rounded-2xl p-6 border border-red-500/20 text-center"
            >
              <p className="text-sm text-white/80 mb-4">{error}</p>
              <Link
                to="/"
                className="text-xs text-scout-blue hover:underline underline-offset-2"
              >
                Open ScoutDex →
              </Link>
            </div>
          )}

          {/* The captured card itself */}
          {data && <CardRenderer payload={data.payload} />}

          {/* Spoken answer captured at share time */}
          {data?.voice_text && (
            <p
              data-testid="snapshot-page-voice"
              className="text-sm text-white/65 leading-relaxed mt-4 px-1"
            >
              {data.voice_text}
            </p>
          )}

          {/* CTA back to chat — always present below the card. */}
          <div className="mt-8 text-center">
            <Link
              to="/"
              className="text-xs text-white/40 hover:text-white/70 underline-offset-2 hover:underline transition-colors"
            >
              Ask Scout anything →
            </Link>
          </div>
        </div>
      </main>
    </>
  );
}
